import { useCallback, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { useModalContext } from 'context/modalContext'
import { someTimeAgo } from 'lib/dayjs'
import { useUserProfile } from 'store'

import styles from './_NotificationCardLayout.module.scss'

interface INotificationCardLayout {
  id?: number
  avatarSrc?: string
  name: string
  atName: string
  profileUrl?: string
  hasDollar?: boolean
  isOnline?: boolean
  time?: string
  isSystem?: boolean
  onBodyClick?: () => void
  children: React.ReactNode
  postThumbnail?: string | null
  showFanOptions?: boolean | null
  canLikeNotification?: boolean
}

const NotificationCardLayout = ({
  id,
  avatarSrc,
  name,
  atName,
  profileUrl,
  hasDollar = false,
  isOnline = false,
  time,
  children,
  isSystem = false,
  onBodyClick,
  postThumbnail,
  showFanOptions
}: INotificationCardLayout) => {
  const modal = useModalContext()
  const me = useUserProfile()
  const navigate = useNavigate()

  // PNG only for notification card display
  const avatarImg = avatarSrc || avatarPlaceholder

  const normalizedUser = useMemo(
    () => ({
      id: id ?? 0,
      display_name: name,
      username: atName,
      avatar: { url: avatarSrc || null }
    }),
    [id, name, atName, avatarSrc]
  )

const handleUserClick = useCallback(() => {
  if (isSystem) return

  if (me?.role === 'model' && showFanOptions === true) {
    const modalKey = `user-details-${normalizedUser.id || atName || 'unknown'}`
    modal.addModal(modalKey, <UserDetails key={modalKey} user={normalizedUser} />, true)
    return
  }

  if (atName) {
    navigate(`/${atName}`)
    return
  }

  if (profileUrl) navigate(profileUrl)
}, [isSystem, me?.role, showFanOptions, modal, normalizedUser, navigate, atName, profileUrl])


const handleBodyClick = useCallback(
  (e: React.MouseEvent) => {
    if (!onBodyClick) return

    const target = e.target as HTMLElement

    if (
      target.closest('a') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('textarea') ||
      target.closest('select')
    ) {
      return
    }

    onBodyClick()
  },
  [onBodyClick]
)

  const stop = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div className={styles.notificationCard}>
      <div
        className={styles.avatarContainer}
        onClick={(e) => {
          if (isSystem) return

          stop(e)
          handleUserClick()
        }}
        style={{
          cursor: isSystem ? 'default' : 'pointer'
        }}
      >
        <AvatarHolder
          img={avatarImg}
          size="60"
          isOnline={isOnline}
          hasDollar={hasDollar}
        />
      </div>

      <div
        className={styles.body}
        onClick={handleBodyClick}
        style={{ cursor: onBodyClick ? 'pointer' : 'default' }}
      >
        <div className={styles.bodyMain}>
          <div className={styles.nameContainer}>
            {isSystem ? (
              <h2 className={`${styles.name} ${styles.isSystem}`}>{name}</h2>
            ) : (
              <div
                onClick={(e) => {
                  stop(e)
                  handleUserClick()
                }}
                style={{ cursor: 'pointer' }}
              >
                <h2 className={styles.name}>{name}</h2>
              </div>
            )}

            {!isSystem && atName && (
              <div
                onClick={(e) => {
                  stop(e)
                  handleUserClick()
                }}
                className={styles.atName}
                style={{ cursor: 'pointer' }}
              >
                @{atName}
              </div>
            )}
          </div>

          <div className={styles.content}>{children}</div>

          {time && <p className={styles.time}>{someTimeAgo(time)}</p>}
        </div>

        {postThumbnail && (
          <div className={styles.postThumbnail}>
            <img src={postThumbnail} alt="" />
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationCardLayout